import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Loader2,
  XCircle,
  FileText,
  Download,
  ClipboardList,
  Package,
  TestTube2,
  CheckCircle2,
} from "lucide-react";

const IST = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
      timeZone: "Asia/Kolkata",
    });
  } catch {
    return iso;
  }
};

const STATUS_META = {
  booked: { label: "Booked", Icon: ClipboardList, cls: "bg-slate-100 text-slate-700" },
  collected: { label: "Sample collected", Icon: Package, cls: "bg-blue-100 text-blue-700" },
  processing: { label: "Processing in lab", Icon: TestTube2, cls: "bg-amber-100 text-amber-700" },
  delivered: { label: "Report ready", Icon: CheckCircle2, cls: "bg-emerald-100 text-emerald-700" },
};

/**
 * Public pathology-report page. Route: /care/report/:orderId?t=<token>
 * No login — the token in the URL is the credential, same pattern as
 * /shop/track/:orderId. Polls while the report isn't ready yet.
 */
export default function CareReport() {
  const { orderId } = useParams();
  const [params] = useSearchParams();
  const token = params.get("t") || "";
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!orderId || !token) {
      setError("This report link is invalid.");
      setLoading(false);
      return;
    }
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get(
          `/healthcare/pathology-orders/${orderId}/report?t=${encodeURIComponent(token)}`,
        );
        if (!cancelled) setOrder(data);
      } catch (err) {
        if (!cancelled) {
          setError(err?.response?.data?.detail || "This report link is invalid or has expired.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    const t = setInterval(load, 20000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [orderId, token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <Card className="max-w-md w-full">
          <CardContent className="p-6 text-center space-y-2">
            <XCircle className="w-8 h-8 text-red-500 mx-auto" />
            <div className="text-lg font-medium">{error}</div>
            <p className="text-sm text-slate-500">Ask the clinic to resend your report link.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const status = order?.status || "booked";
  const meta = STATUS_META[status] || STATUS_META.booked;
  const StatusIcon = meta.Icon;
  const isPdf = order?.report_file?.startsWith("data:application/pdf");

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          <div className="w-8 h-8 rounded bg-[#0f6e64] flex items-center justify-center">
            <FileText className="w-4 h-4 text-white" />
          </div>
          <div>
            <div className="text-sm font-medium">Lab report</div>
            <div className="mono text-[10px] uppercase tracking-widest text-slate-400">
              {order?.patient_name}
              {order?.relation_note ? ` (${order.relation_note})` : ""}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6 space-y-4">
        <div className="flex items-center justify-between">
          <div className="mono text-[11px] uppercase tracking-widest text-slate-500">
            Booked · {IST(order?.created_at)}
          </div>
          <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium ${meta.cls}`} data-testid="report-status">
            <StatusIcon className="w-3.5 h-3.5" />
            {meta.label}
          </span>
        </div>

        <Card>
          <CardContent className="p-4">
            <div className="mono text-[11px] uppercase tracking-widest text-slate-500 mb-2">Tests</div>
            <ul className="space-y-1 text-sm text-slate-700">
              {(order?.tests || []).map((name, i) => (
                <li key={i}>{name}</li>
              ))}
            </ul>
          </CardContent>
        </Card>

        {order?.report_file ? (
          <Card data-testid="report-ready">
            <CardContent className="p-4 space-y-3">
              <div className="mono text-[11px] uppercase tracking-widest text-emerald-700">Your report</div>
              {isPdf ? (
                <embed src={order.report_file} type="application/pdf" className="w-full h-[70vh] rounded border border-slate-200" />
              ) : (
                <img src={order.report_file} alt="Lab report" className="w-full rounded border border-slate-200" />
              )}
              <a href={order.report_file} download={`report-${orderId.slice(0, 8)}${isPdf ? ".pdf" : ".jpg"}`}>
                <Button className="w-full gap-2 bg-[#0f6e64] hover:bg-[#0c5952]">
                  <Download className="w-4 h-4" /> Download report
                </Button>
              </a>
            </CardContent>
          </Card>
        ) : (
          <p className="text-center text-sm text-slate-500 py-6">
            Your report isn't ready yet — this page updates automatically once it is.
          </p>
        )}
      </main>
    </div>
  );
}
